import React, { useState } from 'react';
import { SendIcon, CheckCircle2, Mail, User, Phone, MessageSquare, Sparkles, ChevronDown, AlertCircle, Shield, Clock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Badge } from './ui/Badge';
import confetti from 'canvas-confetti';
interface ContactFormProps {
  serviceId?: string;
}
export function ContactForm({
  serviceId
}: ContactFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: serviceId || '',
    message: '',
    petName: '',
    petType: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const validateField = (name: string, value: string) => {
    let error = '';
    switch (name) {
      case 'name':
        if (!value.trim()) error = 'Por favor, dinos tu nombre';else if (value.trim().length < 2) error = 'El nombre es muy corto';
        break;
      case 'email':
        if (!value.trim()) error = 'Necesitamos tu email para responderte';else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) error = 'Este email no parece válido';
        break;
      case 'phone':
        if (value && !/^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/.test(value)) {
          error = 'El formato del teléfono no es correcto';
        }
        break;
      case 'message':
        if (!value.trim()) error = 'Cuéntanos en qué podemos ayudarte';else if (value.trim().length < 10) error = 'Por favor, danos un poco más de detalle (mínimo 10 caracteres)';
        break;
    }
    return error;
  };
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key as keyof typeof formData]);
      if (error) newErrors[key] = error;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched({
      name: true,
      email: true,
      message: true
    });
    if (!validateForm()) return;
    setIsSubmitting(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    setIsSubmitting(false);
    setIsSubmitted(true);
    try {
      confetti({
        particleCount: 150,
        spread: 70,
        origin: {
          y: 0.6
        },
        colors: ['#2D4A3E', '#C87951', '#F59E0B'],
        disableForReducedMotion: true
      });
    } catch {
      // Silently handle confetti error
    }
  };
  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };
  const handleBlur = (field: string) => {
    setTouched(prev => ({
      ...prev,
      [field]: true
    }));
    const error = validateField(field, formData[field as keyof typeof formData]);
    setErrors(prev => ({
      ...prev,
      [field]: error
    }));
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      handleSubmit(e as unknown as FormEvent);
    }
  };
  if (isSubmitted) {
    return <motion.div initial={{
      opacity: 0,
      scale: 0.9
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      type: 'spring',
      stiffness: 300,
      damping: 25
    }} className="text-center py-20 px-8 bg-gradient-to-br from-emerald-50 via-white to-emerald-50/30 rounded-[2.5rem] border-2 border-emerald-100 shadow-soft-2xl relative overflow-hidden" role="alert" aria-live="polite" data-id="element-1230">
        <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-200/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" data-id="element-1231" />

        <div className="relative z-10" data-id="element-1232">
          <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1,
          rotate: [0, 10, -10, 0]
        }} transition={{
          delay: 0.2,
          type: 'spring',
          stiffness: 500,
          damping: 25
        }} className="w-28 h-28 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-8 shadow-soft-xl" data-id="element-1233">
            <CheckCircle2 className="h-14 w-14 text-white" data-id="element-1234" />
          </motion.div>

          <motion.h3 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="text-3xl md:text-4xl font-bold text-charcoal-900 mb-4 font-serif" data-id="element-1235">
            ¡Mensaje recibido!
          </motion.h3>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className="text-charcoal-600 leading-relaxed text-lg mb-10 max-w-md mx-auto" data-id="element-1236">
            Gracias por contactarnos,{' '}
            <span className="font-bold text-charcoal-900" data-id="element-1237">{formData.name}</span>
            . Nuestro equipo (humano) te responderá en menos de 24 horas.
          </motion.p>

          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.5
        }} className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1238">
            <Button variant="primary" size="lg" onClick={() => {
            setIsSubmitted(false);
            setFormData({
              name: '',
              email: '',
              phone: '',
              subject: serviceId || '',
              message: '',
              petName: '',
              petType: ''
            });
            setTouched({});
            setErrors({});
          }} className="rounded-full shadow-soft-lg hover:shadow-soft-xl" data-id="element-1239">
              Enviar otro mensaje
            </Button>
            <Button variant="outline" size="lg" onClick={() => window.location.href = '/'} className="rounded-full" data-id="element-1240">
              Volver al inicio
            </Button>
          </motion.div>
        </div>
      </motion.div>;
  }
  const formProgress = Object.entries(formData).filter(([key, value]) => ['name', 'email', 'message'].includes(key) && value.trim()).length / 3 * 100;
  return <motion.form initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} onSubmit={handleSubmit} onKeyDown={handleKeyDown} className="space-y-8" noValidate aria-label="Formulario de contacto" data-id="element-1241">
      {/* Progress Bar */}
      <div className="mb-10" aria-hidden="true" data-id="element-1242">
        <div className="flex items-center justify-between mb-3" data-id="element-1243">
          <span className="text-xs font-bold text-charcoal-500 uppercase tracking-wider flex items-center gap-2" data-id="element-1244">
            <Sparkles className="w-3.5 h-3.5 text-primary-600" data-id="element-1245" />
            Progreso
          </span>
          <Badge variant="primary" size="sm" className="font-bold" data-id="element-1246">
            {Math.round(formProgress)}%
          </Badge>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden shadow-inner" data-id="element-1247">
          <motion.div initial={{
          width: 0
        }} animate={{
          width: `${formProgress}%`
        }} transition={{
          duration: 0.5,
          ease: 'easeOut'
        }} className="h-full bg-gradient-to-r from-primary-600 to-primary-400 relative rounded-full" data-id="element-1248" />
        </div>
      </div>

      {/* Personal Information */}
      <div className="space-y-6" data-id="element-1249">
        <h3 className="text-lg font-bold text-charcoal-900 flex items-center gap-2" data-id="element-1250">
          <User className="w-5 h-5 text-primary-600" data-id="element-1251" />
          Información Personal
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1252">
          <Input label="Nombre completo" name="name" value={formData.name} onChange={handleChange} onBlur={() => handleBlur('name')} error={touched.name ? errors.name : ''} success={touched.name && !errors.name && formData.name.trim().length > 0} leftIcon={<User className="h-5 w-5" data-id="element-1254" />} placeholder="Tu nombre" required aria-required="true" data-id="element-1253" />
          <Input label="Correo electrónico" name="email" type="email" value={formData.email} onChange={handleChange} onBlur={() => handleBlur('email')} error={touched.email ? errors.email : ''} success={touched.email && !errors.email && formData.email.trim().length > 0} leftIcon={<Mail className="h-5 w-5" data-id="element-1256" />} placeholder="tu@email.com" required aria-required="true" data-id="element-1255" />
        </div>

        <Input label="Teléfono (opcional)" name="phone" type="tel" value={formData.phone} onChange={handleChange} onBlur={() => handleBlur('phone')} error={touched.phone ? errors.phone : ''} leftIcon={<Phone className="h-5 w-5" data-id="element-1258" />} placeholder="+34 XXX XXX XXX" helperText="Solo te llamaremos si es estrictamente necesario" data-id="element-1257" />
      </div>

      {/* Pet Information (Optional) */}
      <div className="space-y-6 p-6 bg-primary-50/30 rounded-2xl border border-primary-100" data-id="element-1259">
        <h3 className="text-lg font-bold text-charcoal-900 flex items-center gap-2" data-id="element-1260">
          <Sparkles className="w-5 h-5 text-primary-600" data-id="element-1261" />
          Sobre tu Mascota (Opcional)
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1262">
          <Input label="Nombre de tu mascota" name="petName" value={formData.petName} onChange={handleChange} placeholder="Ej: Max, Luna..." data-id="element-1263" />
          <div data-id="element-1264">
            <label htmlFor="petType" className="block text-sm font-bold text-charcoal-900 mb-2" data-id="element-1265">
              Tipo de mascota
            </label>
            <div className="relative group" data-id="element-1266">
              <select id="petType" name="petType" value={formData.petType} onChange={handleChange} className="w-full pl-4 pr-10 py-3.5 border-2 border-gray-200 rounded-xl text-base text-charcoal-900 focus:outline-none focus:border-primary-600 focus:ring-4 focus:ring-primary-600/10 transition-all appearance-none bg-white hover:border-gray-300 cursor-pointer" data-id="element-1267">
                <option value="" data-id="element-1268">Selecciona</option>
                <option value="dog" data-id="element-1269">Perro</option>
                <option value="cat" data-id="element-1270">Gato</option>
                <option value="other" data-id="element-1271">Otro</option>
              </select>
              <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" data-id="element-1272" />
            </div>
          </div>
        </div>
      </div>

      {/* Message Details */}
      <div className="space-y-6" data-id="element-1273">
        <h3 className="text-lg font-bold text-charcoal-900 flex items-center gap-2" data-id="element-1274">
          <MessageSquare className="w-5 h-5 text-primary-600" data-id="element-1275" />
          Tu Consulta
        </h3>

        <div data-id="element-1276">
          <label htmlFor="subject" className="block text-sm font-bold text-charcoal-900 mb-2" data-id="element-1277">
            Asunto
          </label>
          <div className="relative group" data-id="element-1278">
            <MessageSquare className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 group-hover:text-primary-600 transition-colors pointer-events-none" data-id="element-1279" />
            <select id="subject" name="subject" value={formData.subject} onChange={handleChange} className="w-full pl-12 pr-10 py-3.5 border-2 border-gray-200 rounded-xl text-base text-charcoal-900 focus:outline-none focus:border-primary-600 focus:ring-4 focus:ring-primary-600/10 transition-all appearance-none bg-white hover:border-gray-300 cursor-pointer" data-id="element-1280">
              <option value="" data-id="element-1281">Selecciona un asunto</option>
              <option value="general" data-id="element-1282">Consulta general</option>
              <option value="products" data-id="element-1283">Información sobre productos</option>
              <option value="nutritional-consulting" data-id="element-1284">
                Asesoramiento nutricional
              </option>
              <option value="health-wellness" data-id="element-1285">Salud y bienestar</option>
              <option value="orders" data-id="element-1286">Pedidos y envíos</option>
              <option value="returns" data-id="element-1287">Devoluciones y cambios</option>
              <option value="subscription" data-id="element-1288">Suscripciones</option>
              <option value="veterinary" data-id="element-1289">Consulta veterinaria</option>
              <option value="other" data-id="element-1290">Otro</option>
            </select>
            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" data-id="element-1291" />
          </div>
        </div>

        <div data-id="element-1292">
          <label htmlFor="message" className="block text-sm font-bold text-charcoal-900 mb-2" data-id="element-1293">
            Mensaje *
          </label>
          <div className="relative group" data-id="element-1294">
            <textarea id="message" name="message" value={formData.message} onChange={handleChange} onBlur={() => handleBlur('message')} rows={6} maxLength={1000} className={`w-full px-5 py-4 border-2 rounded-2xl text-base focus:outline-none focus:ring-4 transition-all resize-none ${touched.message && errors.message ? 'border-rose-300 focus:border-rose-500 focus:ring-rose-500/10 bg-rose-50/30' : 'border-gray-200 focus:border-primary-600 focus:ring-primary-600/10 hover:border-gray-300'}`} placeholder="¿En qué podemos ayudarte? Cuéntanos con detalle tu consulta..." aria-required="true" aria-invalid={!!(touched.message && errors.message)} aria-describedby={touched.message && errors.message ? 'message-error' : undefined} data-id="element-1295" />
            <div className="absolute bottom-4 right-4 text-xs text-charcoal-400 font-bold bg-white/90 px-3 py-1.5 rounded-lg backdrop-blur-sm shadow-sm border border-gray-100" data-id="element-1296">
              {formData.message.length}/1000
            </div>
          </div>
          <AnimatePresence data-id="element-1297">
            {touched.message && errors.message && <motion.p id="message-error" initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} className="mt-2 text-sm text-rose-600 font-medium flex items-center gap-2" role="alert" data-id="element-1298">
                <AlertCircle className="w-4 h-4" data-id="element-1299" />
                {errors.message}
              </motion.p>}
          </AnimatePresence>
        </div>
      </div>

      {/* Submit Button */}
      <motion.div whileHover={{
      scale: 1.01
    }} whileTap={{
      scale: 0.99
    }} data-id="element-1300">
        <Button type="submit" variant="primary" size="xl" disabled={isSubmitting} fullWidth className="h-16 text-lg font-bold shadow-soft-xl hover:shadow-soft-2xl transition-all rounded-2xl" isLoading={isSubmitting} loadingText="Enviando mensaje..." data-id="element-1301">
          {!isSubmitting && <>
              <SendIcon className="h-5 w-5 mr-2" data-id="element-1302" />
              Enviar mensaje
              <Sparkles className="h-4 w-4 ml-2" data-id="element-1303" />
            </>}
        </Button>
      </motion.div>

      {/* Footer Info */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-gray-100" data-id="element-1304">
        <p className="text-xs text-charcoal-500 text-center sm:text-left leading-relaxed" data-id="element-1305">
          Al enviar aceptas nuestra{' '}
          <a href="/privacidad" className="text-primary-600 hover:underline font-bold focus:outline-none focus:ring-2 focus:ring-primary-600 rounded" data-id="element-1306">
            política de privacidad
          </a>
        </p>
        <div className="flex items-center gap-4 text-xs text-charcoal-500 font-medium" data-id="element-1307">
          <div className="flex items-center gap-1.5" data-id="element-1308">
            <Clock className="w-3.5 h-3.5 text-primary-600" data-id="element-1309" />
            <span data-id="element-1310">Respuesta en 24h</span>
          </div>
          <div className="flex items-center gap-1.5" data-id="element-1311">
            <Shield className="w-3.5 h-3.5 text-emerald-600" data-id="element-1312" />
            <span data-id="element-1313">Datos seguros</span>
          </div>
        </div>
      </div>
    </motion.form>;
}